import { useState, type FormEvent } from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Folder,
  LoaderCircle,
  Monitor,
  Plus,
  Trash2,
} from "lucide-react";
import type { Lane, Project } from "../../../server/src/models";
import type { Settings } from "@mk/core";
import { api } from "../api";
import { laneColor, IconButton, Modal } from "./ui";

export function SettingsPanel({
  project,
  lanes,
  initialTab,
  onClose,
  onSaved,
}: {
  project: Project;
  lanes: Lane[];
  initialTab: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [tab, setTab] = useState(initialTab),
    [settings, setSettings] = useState<Settings>(
      structuredClone(project.settings),
    ),
    [draftLanes, setDraftLanes] = useState(lanes.map((l) => ({ ...l }))),
    [moves, setMoves] = useState<Record<string, string>>({}),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [machineName, setMachineName] = useState(""),
    [newLane, setNewLane] = useState("");
  const [projectName, setProjectName] = useState(project.name),
    [baseBranch, setBaseBranch] = useState(project.base_branch);
  const change = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setSettings((s) => ({ ...s, [key]: value }));
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (tab === "lanes")
        await api(`/v1/projects/${project.key}/lanes`, "PUT", {
          lanes: draftLanes,
          moves,
        });
      else
        await api(`/v1/projects/${project.key}/settings`, "PUT", {
          ...settings,
          ready_check: settings.ready_check
            .map((text) => text.trim())
            .filter(Boolean),
        });
      if (
        tab === "guidance" &&
        (projectName !== project.name || baseBranch !== project.base_branch)
      )
        await api(`/v1/projects/${project.key}`, "PATCH", {
          name: projectName,
          base_branch: baseBranch,
        });
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const sections = [
    ["guidance", "Project guidance", "What every run should know"],
    ["workflow", "Workflow", "Instructions and ready checks"],
    ["lanes", "Board lanes", "Shape your flow"],
    ["machines", "Machines", "Where the work happens"],
    ["limits", "Limits & archive", "Timing, files, and memory"],
  ];
  return (
    <Modal title="Project settings" wide onClose={onClose}>
      <div className="settings-layout">
        <nav className="settings-nav">
          <div className="settings-project">
            <Folder size={18} />
            <span>
              {project.name}
              <small className="mono">
                {project.prefix} · {project.base_branch}
              </small>
            </span>
          </div>
          {sections.map(([key, title, description]) => (
            <button
              className={tab === key ? "active" : ""}
              key={key}
              onClick={() => setTab(key)}
            >
              <b>{title}</b>
              <small>{description}</small>
            </button>
          ))}
        </nav>
        <form className="settings-body" onSubmit={save}>
          {tab === "guidance" && (
            <>
              <span className="eyebrow">A SHARED STARTING POINT</span>
              <h2>Project guidance</h2>
              <div className="form-grid">
                <label>
                  Project name
                  <input
                    value={projectName}
                    required
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                </label>
                <label>
                  Base branch
                  <input
                    value={baseBranch}
                    required
                    onChange={(e) => setBaseBranch(e.target.value)}
                  />
                </label>
              </div>
              <p className="section-description">
                Every agent reads this before its task. Keep project-wide
                conventions here, and task-specific knowledge in context.
              </p>
              <label>
                Guidance
                <textarea
                  className="guidance-editor"
                  rows={15}
                  value={settings.guidance}
                  onChange={(e) => change("guidance", e.target.value)}
                />
              </label>
              <p className="subtle">
                Plain text and Markdown are welcome. Changes appear in the next
                briefing.
              </p>
            </>
          )}
          {tab === "workflow" && (
            <>
              <h2>One clear instruction</h2>
              <p className="section-description">
                Set a useful starting line for the next run in each lane. Use{" "}
                {"{id}"} for the task ID and {"{base}"} for the base branch.
              </p>
              {draftLanes.map((l) => (
                <label key={l.key}>
                  {l.title}
                  <input
                    value={settings.lane_templates[l.key] ?? ""}
                    onChange={(e) =>
                      change("lane_templates", {
                        ...settings.lane_templates,
                        [l.key]: e.target.value,
                      })
                    }
                  />
                </label>
              ))}
              <h3>Ready check</h3>
              <p className="section-description">
                Advice for triage runs. You decide whether the task is ready.
              </p>
              <label>
                One check per line
                <textarea
                  rows={5}
                  value={settings.ready_check.join("\n")}
                  onChange={(e) =>
                    change("ready_check", e.target.value.split("\n"))
                  }
                />
              </label>
            </>
          )}
          {tab === "lanes" && (
            <>
              <h2>A board that fits your work</h2>
              <p className="section-description">
                The five system lanes stay. Add review or testing stages after
                Ready. Clock controls whether time in that lane counts.
              </p>
              <div className="lane-editor">
                {draftLanes.map((l, i) => (
                  <div className="lane-editor-row" key={l.key}>
                    <span className={`dot ${laneColor(l.key)}`} />
                    <input
                      aria-label={`Title for ${l.key}`}
                      value={l.title}
                      required
                      onChange={(e) =>
                        setDraftLanes(
                          draftLanes.map((x, n) =>
                            n === i ? { ...x, title: e.target.value } : x,
                          ),
                        )
                      }
                    />
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={l.clock}
                        onChange={(e) =>
                          setDraftLanes(
                            draftLanes.map((x, n) =>
                              n === i ? { ...x, clock: e.target.checked } : x,
                            ),
                          )
                        }
                      />
                      Clock
                    </label>
                    <IconButton
                      label={`Move ${l.title} up`}
                      disabled={i < 3}
                      onClick={() => {
                        const next = [...draftLanes];
                        [next[i - 1], next[i]] = [next[i], next[i - 1]];
                        setDraftLanes(next);
                      }}
                    >
                      <ArrowUp size={13} />
                    </IconButton>
                    <IconButton
                      label={`Move ${l.title} down`}
                      disabled={i < 2 || i === draftLanes.length - 1}
                      onClick={() => {
                        const next = [...draftLanes];
                        [next[i + 1], next[i]] = [next[i], next[i + 1]];
                        setDraftLanes(next);
                      }}
                    >
                      <ArrowDown size={13} />
                    </IconButton>
                    {l.system ? (
                      <span className="system-lane">System</span>
                    ) : (
                      <IconButton
                        label={`Remove ${l.title}`}
                        onClick={() =>
                          setDraftLanes(
                            draftLanes.filter((x) => x.key !== l.key),
                          )
                        }
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    )}
                  </div>
                ))}
              </div>
              {lanes
                .filter((l) => !draftLanes.some((d) => d.key === l.key))
                .map((l) => (
                  <label key={l.key}>
                    Move tasks from {l.title} to
                    <select
                      value={moves[l.key] ?? ""}
                      required
                      onChange={(e) =>
                        setMoves({ ...moves, [l.key]: e.target.value })
                      }
                    >
                      <option value="">Choose destination</option>
                      {draftLanes.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.title}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              <div className="add-lane-form">
                <input
                  aria-label="New lane title"
                  placeholder="e.g. Review"
                  value={newLane}
                  onChange={(e) => setNewLane(e.target.value)}
                />
                <button
                  type="button"
                  className="button"
                  disabled={!newLane.trim()}
                  onClick={() => {
                    const key = newLane
                      .toLowerCase()
                      .trim()
                      .replace(/[^a-z0-9]+/g, "_")
                      .replace(/^_|_$/g, "");
                    if (
                      !/^[a-z]/.test(key) ||
                      draftLanes.some((l) => l.key === key)
                    ) {
                      setError(
                        "Choose a unique lane name starting with a letter.",
                      );
                      return;
                    }
                    setDraftLanes([
                      ...draftLanes,
                      {
                        key,
                        title: newLane.trim(),
                        clock: !["discarded"].includes(key),
                        collapsed: false,
                        system: false,
                        project: project.key,
                        position: draftLanes.length,
                      },
                    ]);
                    setNewLane("");
                    setError("");
                  }}
                >
                  <Plus size={14} />
                  Add lane
                </button>
              </div>
            </>
          )}
          {tab === "machines" && (
            <>
              <h2>Give each session its bearings</h2>
              <p className="section-description">
                Names must match the machine identity sent by the agent. Editing
                runs use the checkout path to prevent overlapping edits.
              </p>
              {Object.entries(settings.machines).map(([name, m]) => (
                <div className="machine-editor" key={name}>
                  <div className="section-heading">
                    <h3>
                      <Monitor size={15} />
                      {name}
                    </h3>
                    <IconButton
                      label={`Remove machine ${name}`}
                      onClick={() =>
                        change(
                          "machines",
                          Object.fromEntries(
                            Object.entries(settings.machines).filter(
                              ([k]) => k !== name,
                            ),
                          ),
                        )
                      }
                    >
                      <Trash2 size={13} />
                    </IconButton>
                  </div>
                  <label>
                    Repository path
                    <input
                      value={m.repo_path}
                      onChange={(e) =>
                        change("machines", {
                          ...settings.machines,
                          [name]: { ...m, repo_path: e.target.value },
                        })
                      }
                    />
                  </label>
                  <label>
                    Available tools
                    <input
                      value={m.tools}
                      onChange={(e) =>
                        change("machines", {
                          ...settings.machines,
                          [name]: { ...m, tools: e.target.value },
                        })
                      }
                    />
                  </label>
                  <label>
                    Notes
                    <textarea
                      rows={2}
                      value={m.notes}
                      onChange={(e) =>
                        change("machines", {
                          ...settings.machines,
                          [name]: { ...m, notes: e.target.value },
                        })
                      }
                    />
                  </label>
                </div>
              ))}
              <div className="add-lane-form">
                <input
                  aria-label="New machine name"
                  value={machineName}
                  placeholder="e.g. macbook"
                  onChange={(e) => setMachineName(e.target.value)}
                />
                <button
                  type="button"
                  className="button"
                  disabled={
                    !machineName.trim() ||
                    !!settings.machines[machineName.trim()]
                  }
                  onClick={() => {
                    change("machines", {
                      ...settings.machines,
                      [machineName.trim()]: {
                        repo_path: "",
                        tools: "",
                        notes: "",
                      },
                    });
                    setMachineName("");
                  }}
                >
                  <Plus size={14} />
                  Add machine
                </button>
              </div>
            </>
          )}
          {tab === "limits" && (
            <>
              <h2>A little room, sensible limits</h2>
              <p className="section-description">
                A stale run is a signal. An expired session releases its claims.
                Returning agents receive a fresh briefing.
              </p>
              <div className="form-grid">
                <label>
                  Stale after (minutes)
                  <input
                    type="number"
                    min={1}
                    max={1440}
                    value={settings.stale_after_minutes}
                    onChange={(e) =>
                      change("stale_after_minutes", Number(e.target.value))
                    }
                  />
                </label>
                <label>
                  Expire after (minutes)
                  <input
                    type="number"
                    min={2}
                    max={10080}
                    value={settings.session_idle_timeout_minutes}
                    onChange={(e) =>
                      change(
                        "session_idle_timeout_minutes",
                        Number(e.target.value),
                      )
                    }
                  />
                </label>
                <label>
                  Attachment limit (MiB)
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={settings.attachment_max_mb}
                    onChange={(e) =>
                      change("attachment_max_mb", Number(e.target.value))
                    }
                  />
                </label>
                <label>
                  Briefing budget (characters)
                  <input
                    type="number"
                    min={4000}
                    max={100000}
                    value={settings.briefing_budget}
                    onChange={(e) =>
                      change("briefing_budget", Number(e.target.value))
                    }
                  />
                </label>
                <label>
                  Archive finished tasks after (days)
                  <input
                    type="number"
                    min={0}
                    max={3650}
                    value={settings.archive_days}
                    onChange={(e) =>
                      change("archive_days", Number(e.target.value))
                    }
                  />
                  <small>0 keeps finished tasks on the board.</small>
                </label>
              </div>
              <h3>Reported spending</h3>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.show_spend}
                  onChange={(e) => change("show_spend", e.target.checked)}
                />
                Show tokens and cost when supplied by the harness
              </label>
              <div className="form-grid">
                <label>
                  Run token warning (0 disables)
                  <input
                    type="number"
                    min={0}
                    value={settings.warn_tokens}
                    onChange={(e) =>
                      change("warn_tokens", Number(e.target.value))
                    }
                  />
                </label>
                <label>
                  Task spend warning, USD (0 disables)
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={settings.warn_usd}
                    onChange={(e) => change("warn_usd", Number(e.target.value))}
                  />
                </label>
              </div>
            </>
          )}
          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
          <div className="settings-save">
            <span className="subtle">
              Saved changes are available to the next run.
            </span>
            <button className="button primary" disabled={busy}>
              {busy ? (
                <LoaderCircle size={14} className="spin" />
              ) : (
                <Check size={14} />
              )}
              Save {tab === "lanes" ? "lanes" : "settings"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
